import { useCallback, useEffect, useState } from "react";

import { Box, Collapse } from "@mui/material";
import { usePostHog } from "posthog-js/react";
import qs from "qs";

import {
  useAPI,
  useRoleContext,
  useShipmentFiltersContext,
  useSubscriptionStatus,
} from "hooks";
import {
  getPageCount,
  isEssentialsV2Carrier,
  SHIPMENTS_PAGE_SIZE,
} from "utils";

import { useShipments } from "../../../../hooks/useShipments";
import { PublicShipmentsLockedOverlay } from "../PublicShipmentsLockedOverlay";
import { ShipmentsList } from "../ShipmentList";
import { ShipmentsFilter } from "../ShipmentsFilter";
import { ShipmentsTabs } from "../ShipmentsTabs";

export function ShipmentListContainer() {
  const [page, setPage] = useState<number>(1);
  const {
    filters,
    updateShipmentRelation,
    registerRefreshCallback,
    unregisterRefreshCallback,
  } = useShipmentFiltersContext();
  const [prevFilters, setPrevFilters] = useState(filters);

  if (prevFilters !== filters) {
    setPrevFilters(filters);
    setPage(1);
  }
  const { shipments, error, count, isLoading, refreshShipments } = useShipments(
    { page, pageSize: SHIPMENTS_PAGE_SIZE },
  );
  const pages = getPageCount(count, SHIPMENTS_PAGE_SIZE);

  // Dual count fetching for tabs
  const [partnerShipmentCount, setPartnerShipmentCount] = useState<number>(0);
  const [publicShipmentCount, setPublicShipmentCount] = useState<number>(0);
  const [countError, setCountError] = useState<string | null>(null);

  const { role } = useRoleContext();
  const { subscription } = useSubscriptionStatus();
  const { api } = useAPI();
  const posthog = usePostHog();

  const isEssentialsV2 = isEssentialsV2Carrier(subscription);
  const activeTab = filters?.shipmentRelationship ?? "partner";
  const showPublicShipmentsLockedOverlay =
    isEssentialsV2 && activeTab === "public";

  const fetchShipmentCounts = useCallback(() => {
    if (role && filters && subscription) {
      const filterParams = {
        equipment_type: filters.equipment_types ?? null,
        service: filters.services ?? null,
        commodity: filters.commodity?.code ?? null,
        status: filters.status ?? null,
        order_by: filters.order_by ?? null,
        origin_province: filters.origin?.provinces ?? null,
        origin_country: filters.origin?.countries ?? null,
        destination_province: filters.destination?.provinces ?? null,
        destination_country: filters.destination?.countries ?? null,
        shipment_name:
          filters.shipmentName && filters.shipmentName !== ""
            ? filters.shipmentName
            : null,
      };

      const queryString = qs.stringify(filterParams, {
        skipNulls: true,
        arrayFormat: "repeat",
      });
      const queryParams = queryString ? `?${queryString}` : "";

      const partnerCountUrl = `/carriers/${role.id}/shipments/partner/count${queryParams}`;
      const publicCountUrl = `/shipments/public/count${queryParams}`;

      Promise.all([
        api<{ count: number }>("get", partnerCountUrl),
        api<{ count: number }>("get", publicCountUrl),
      ])
        .then(([partnerResponse, publicResponse]) => {
          setPartnerShipmentCount(partnerResponse.data.count);
          setPublicShipmentCount(publicResponse.data.count);
          setCountError(null);
        })
        .catch((e) => {
          setCountError(
            e?.response?.data?.message ?? "Failed to load shipment counts",
          );
        });
    }
  }, [role, api, filters, subscription]);

  // Fetch counts for both tabs
  useEffect(() => {
    fetchShipmentCounts();
  }, [fetchShipmentCounts]);

  useEffect(() => {
    registerRefreshCallback(refreshShipments);
    return () => unregisterRefreshCallback();
  }, [registerRefreshCallback, unregisterRefreshCallback, refreshShipments]);

  const handlePageChange = (
    _e: React.ChangeEvent<unknown>,
    value: number,
  ): void => {
    setPage(value);
  };

  const handleTabChange = (tab: "partner" | "public"): void => {
    // Track analytics for tab selection
    posthog?.capture("CARRIER_SHIPMENT_TAB_CLICKED", {
      tab_selected: tab,
      partner_shipment_count: partnerShipmentCount,
      public_shipment_count: publicShipmentCount,
      applied_filters: {
        equipment_types: filters?.equipment_types ?? [],
        services: filters?.services ?? [],
        commodity: filters?.commodity?.code ?? null,
        status: filters?.status ?? null,
        order_by: filters?.order_by ?? null,
        origin_provinces: filters?.origin?.provinces ?? [],
        origin_countries: filters?.origin?.countries ?? [],
        destination_provinces: filters?.destination?.provinces ?? [],
        destination_countries: filters?.destination?.countries ?? [],
        shipment_name: filters?.shipmentName ?? null,
      },
    });

    updateShipmentRelation(tab);
    setPage(1); // Reset to first page when changing tabs
  };

  const handleRefresh = (): void => {
    refreshShipments();
    fetchShipmentCounts();
  };

  return (
    <>
      <ShipmentsTabs
        activeTab={activeTab}
        partnerShipmentCount={partnerShipmentCount}
        publicShipmentCount={publicShipmentCount}
        onTabChange={handleTabChange}
        onRefreshClick={handleRefresh}
      />
      <Box px={2} py={1}>
        <ShipmentsFilter />
      </Box>
      <Collapse in={!showPublicShipmentsLockedOverlay} timeout={300}>
        <ShipmentsList
          shipments={shipments as CarrierCondensedShipment[]}
          error={error}
          countError={countError}
          handlePageSelected={handlePageChange}
          pages={pages}
          isLoading={isLoading}
          page={page}
          partnerShipmentCount={partnerShipmentCount}
          publicShipmentCount={publicShipmentCount}
        />
      </Collapse>
      <Collapse in={showPublicShipmentsLockedOverlay} timeout={300}>
        <PublicShipmentsLockedOverlay
          publicShipmentCount={publicShipmentCount}
        />
      </Collapse>
    </>
  );
}
